import { useState } from 'react';

import PnlSettingContent from '@flipster/features/src/shared/pnl-setting-modal/pnl-setting-content';
import { FormattedMessage } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system/modal';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { getErrorMessage } from '@flipster/utils/error';

import DisplayedLeverage from '@web/app/[locale]/(web)/_modal/TradeSettingsModal/Settings/displayed-leverage';
import OrderTutorialV2Content from '@web/app/[locale]/(web)/trade/_misc/order-form/perpetual/Onboarding/OrderTutorialV2Content';
import { DefaultLeverageMarginModeContent } from '@web/app/[locale]/(web)/trade/_misc/order-form/perpetual/modals/default-leverage-margin-mode-modal';

import { NewUserTutorialItems } from '../../trade/_misc/order-form/perpetual/Onboarding/constants';
import { useOrderTutorialV2FeatureTooltip } from '../../trade/_misc/order-form/perpetual/Onboarding/use-order-tutorial-v2-feature-tooltip';
import { useOrderTutorialV2NewFeatureTooltip } from '../../trade/_misc/order-form/perpetual/Onboarding/use-order-tutorial-v2-new-feature-tooltip';
import PositionModes from './Settings/PositionModes';
import TradeSettings from './TradeSettings';
import { SettingType } from './types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  initialType?: SettingType;
  onSuccess: () => void;
  onExit: () => void;
}

const TradeSettingsModal = ({ isOpen, onClose, initialType = 'init', onSuccess, onExit }: Props) => {
  // (noel) I don't use atom because handle initial settingType with side effect should have more complexity of modal
  // and it should reset before closing, it's little bit confused for me.
  const [settingType, setSettingType] = useState<SettingType>(initialType);
  const [tutorialStep, setTutorialStep] = useState(0);

  const [, setOrderTutorialTooltipEnabled] = useOrderTutorialV2FeatureTooltip();
  const [, setOrderTutorialNewFeatureTooltipEnabled] = useOrderTutorialV2NewFeatureTooltip();

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal.Root isOpen={isOpen} onClose={handleClose} onExit={onExit}>
      {settingType === 'orderTutorial' && (
        <OrderTutorialV2Content
          step={tutorialStep}
          onStepChange={setTutorialStep}
          items={NewUserTutorialItems}
          onClickPositionModes={() => setSettingType('positionMode')}
          onFinish={handleClose}
        />
      )}
      {settingType === 'init' && (
        <TradeSettings
          onSelectType={(type) => {
            setSettingType(type);
            if (type === 'orderTutorial') {
              setOrderTutorialTooltipEnabled(false);
              setOrderTutorialNewFeatureTooltipEnabled(false);
            }
          }}
        />
      )}
      {settingType === 'positionMode' && (
        <PositionModes onBack={() => setSettingType('init')} onSuccess={onSuccess} onError={handleClose} />
      )}
      {settingType === 'displayedLeverage' && (
        <DisplayedLeverage
          onBack={() => setSettingType('init')}
          onSuccess={onSuccess}
          onError={(error) => {
            snackbar.error({ message: getErrorMessage(error) });
            handleClose();
          }}
        />
      )}
      {settingType === 'displayedPnl' && (
        <>
          <Modal.Header>
            <Modal.Title label={<FormattedMessage id='Autogen_common.r251126.s_Set_PnL_display' />} />
          </Modal.Header>
          <PnlSettingContent onBack={() => setSettingType('init')} onClose={handleClose} />
        </>
      )}
      {settingType === 'defaultLeverageMarginMode' && (
        <DefaultLeverageMarginModeContent onBack={() => setSettingType('init')} afterConfirm={handleClose} />
      )}
    </Modal.Root>
  );
};

export default TradeSettingsModal;
